import React, { useState, useEffect } from 'react'
import styled, { keyframes, css } from 'styled-components'
import colors from '../utils/style/colors'
import { stockStatusOf, STOCK_META } from '../utils/stockStatus'
import { supabase } from '../supabase'

const bgShift = keyframes`from{filter:brightness(1)}to{filter:brightness(1.1)saturate(1.1)}`
const slideInRight = keyframes`from{opacity:0;transform:translateX(32px)}to{opacity:1;transform:translateX(0)}`
const slideInLeft = keyframes`from{opacity:0;transform:translateX(-32px)}to{opacity:1;transform:translateX(0)}`
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`
const checkAnim = keyframes`0%{transform:scale(0)rotate(-20deg);opacity:0}60%{transform:scale(1.2)rotate(3deg)}100%{transform:scale(1)rotate(0);opacity:1}`

const MiniHero = styled.section`position:relative;height:160px;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#0e1a0a;@media(max-width:768px){height:130px}`
const HeroBg = styled.div`position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 20% 60%,rgba(58,90,46,.5) 0%,transparent 65%),radial-gradient(ellipse 70% 50% at 80% 40%,rgba(107,76,42,.35) 0%,transparent 60%),linear-gradient(160deg,#0e1a0a 0%,#1e2e14 40%,#2a1a0a 100%);animation:${bgShift} 10s ease-in-out infinite alternate`
const HeroGrain = styled.div`position:absolute;inset:0;pointer-events:none;opacity:.05;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");background-size:200px`
const HeroContent = styled.div`position:relative;z-index:2;text-align:center`
const HeroBadge = styled.span`display:inline-block;background:rgba(200,168,75,.15);border:1px solid rgba(200,168,75,.4);color:#e8c96b;font-size:.7rem;font-weight:700;letter-spacing:.18em;text-transform:uppercase;padding:6px 16px;border-radius:2px;margin-bottom:12px;font-family:${colors.font}`
const HeroTitle = styled.h1`font-family:'Playfair Display',Georgia,serif;font-size:clamp(1.6rem,4vw,2.6rem);font-weight:900;color:#fff;line-height:1.1;margin:0 0 8px;text-shadow:0 4px 24px rgba(0,0,0,.5);em{font-style:normal;color:#e8c96b}`
const HeroSub = styled.p`font-family:${colors.font};font-size:.88rem;color:rgba(255,255,255,.6);font-weight:300;margin:0`
const HeroBar = styled.div`position:absolute;bottom:0;left:0;right:0;height:4px;background:linear-gradient(90deg,transparent,#c8a84b,transparent)`

const PageWrap = styled.div`min-height:60vh;background:${colors.beige};display:flex;align-items:flex-start;justify-content:center;padding:60px 20px 80px;@media(max-width:768px){padding:30px 16px 60px}`
const Card = styled.div`background:#faf8f4;border-radius:20px;box-shadow:0 20px 60px rgba(42,32,24,.12);width:100%;max-width:680px;overflow:hidden`

const ProgressBar = styled.div`height:4px;background:#e8d5b8`
const ProgressFill = styled.div`height:100%;background:linear-gradient(90deg,${colors.vertOlive},${colors.or});width:${p=>p.$pct}%;transition:width .5s cubic-bezier(.4,0,.2,1);border-radius:0 2px 2px 0`
const ProgressLabel = styled.div`font-family:${colors.font};font-size:.68rem;font-weight:600;color:${colors.texteLeger};letter-spacing:.08em;text-transform:uppercase;text-align:right;padding:5px 40px 0;@media(max-width:500px){padding:5px 20px 0}`

const SavedBadge = styled.div`display:flex;align-items:center;gap:5px;font-family:${colors.font};font-size:.68rem;font-weight:600;color:${colors.vertOlive};letter-spacing:.06em;text-transform:uppercase;padding:6px 16px;background:rgba(58,90,46,.06);border-bottom:1px solid rgba(58,90,46,.1);animation:${fadeIn} .3s ease both`

const StepperWrap = styled.div`display:flex;align-items:center;padding:20px 40px 16px;background:#fff;border-bottom:1px solid #e8d5b8;@media(max-width:500px){padding:14px 20px}`
const StepCircle = styled.div`width:34px;height:34px;min-width:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.82rem;font-weight:700;font-family:${colors.font};transition:all .3s;${p=>p.$done?css`background:${colors.vertOlive};color:white;border:2px solid ${colors.vertOlive}`:p.$active?css`background:${colors.or};color:${colors.texte};border:2px solid ${colors.or}`:css`background:transparent;color:#bbb;border:2px solid #ddd`}`
const StepLabel = styled.span`font-size:.75rem;font-weight:600;font-family:${colors.font};letter-spacing:.04em;margin-left:7px;color:${p=>p.$active?colors.or:p.$done?colors.vertOlive:'#bbb'};transition:color .3s;@media(max-width:500px){display:none}`
const StepLine = styled.div`flex:1;height:2px;margin:0 10px;background:${p=>p.$done?colors.vertOlive:'#e0d4c0'};transition:background .4s;border-radius:2px`

const CardBody = styled.div`padding:32px 40px 36px;animation:${p=>p.$dir==='back'?slideInLeft:slideInRight} .3s ease both;@media(max-width:500px){padding:22px 20px 28px}`
const StepTitle = styled.h2`font-family:'Playfair Display',Georgia,serif;font-size:1.25rem;font-weight:700;color:${colors.primary};margin-bottom:4px`
const StepDesc = styled.p`font-size:.83rem;color:${colors.greentext};opacity:.72;margin-bottom:22px;font-family:${colors.font};line-height:1.55`

const FieldGrid = styled.div`display:grid;grid-template-columns:${p=>p.$cols===2?'1fr 1fr':'1fr'};gap:14px;@media(max-width:560px){grid-template-columns:1fr}`
const Field = styled.div`display:flex;flex-direction:column;gap:5px`
const Label = styled.label`font-size:.74rem;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${colors.greentext};font-family:${colors.font};opacity:.85;display:flex;align-items:center;gap:6px`
const Optional = styled.span`font-size:.68rem;font-weight:400;opacity:.55;text-transform:none;letter-spacing:0`
const inputBase = css`background:#fff;border:1.5px solid ${p=>p.$error?'#e05a5a':p.$valid?colors.vertOlive:'#e0d4c0'};border-radius:8px;padding:11px 14px;font-size:.9rem;font-family:${colors.font};color:${colors.primary};outline:none;transition:border-color .2s,box-shadow .2s;width:100%;&:focus{border-color:${p=>p.$error?'#e05a5a':colors.vertOlive};box-shadow:0 0 0 3px ${p=>p.$error?'rgba(224,90,90,.1)':'rgba(66,126,94,.1)'}}`
const Input = styled.input`${inputBase}`
const Select = styled.select`${inputBase}`
const Textarea = styled.textarea`${inputBase}resize:vertical;min-height:80px`
const ErrorMsg = styled.span`font-family:${colors.font};font-size:.72rem;color:#e05a5a;display:flex;align-items:center;gap:4px;animation:${fadeIn} .2s ease both`

const ProductGrid = styled.div`display:grid;grid-template-columns:repeat(2,1fr);gap:10px;@media(max-width:500px){grid-template-columns:1fr}`
const ProductChoice = styled.div`border:2px solid ${p=>p.$sel?colors.vertOlive:'#e0d4c0'};border-radius:10px;padding:14px;cursor:pointer;background:${p=>p.$sel?'rgba(58,90,46,.06)':'#fff'};transition:all .2s;display:flex;align-items:center;gap:10px;&:hover{border-color:${colors.vertOlive};background:rgba(58,90,46,.04)}`
const ProdIcon = styled.div`font-size:1.5rem;width:40px;height:40px;background:${p=>p.$sel?colors.vertOlive:'#f0ebe0'};border-radius:8px;display:flex;align-items:center;justify-content:center;transition:background .2s`
const ProdInfo = styled.div``
const ProdName = styled.div`font-weight:700;font-size:.87rem;color:${colors.primary};font-family:${colors.font}`
const ProdType = styled.div`font-size:.73rem;color:${colors.greentext};opacity:.65;font-family:${colors.font}`
const ProdStockTag = styled.div`display:inline-block;margin-top:3px;font-size:.66rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#fff;background:${p=>p.$bg};padding:2px 7px;border-radius:3px;font-family:${colors.font}`
const CheckMark = styled.div`margin-left:auto;width:20px;height:20px;background:${colors.vertOlive};border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.72rem;animation:${checkAnim} .3s ease both`
const CoupeWrap = styled.div`margin-top:8px;padding:12px 14px;background:rgba(58,90,46,.04);border:1px solid rgba(58,90,46,.14);border-radius:8px;animation:${fadeIn} .25s ease both`
const CoupeLabel = styled.div`font-family:${colors.font};font-size:.7rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${colors.vertOlive};margin-bottom:8px`
const CoupeBtns = styled.div`display:flex;flex-wrap:wrap;gap:7px`
const CoupeBtn = styled.button`font-family:${colors.font};font-size:.76rem;font-weight:600;padding:5px 13px;border-radius:20px;cursor:pointer;transition:all .18s;border:1.5px solid;border-color:${p=>p.$active?colors.or:'#d0c4b0'};background:${p=>p.$active?colors.or:'transparent'};color:${p=>p.$active?colors.texte:colors.texteLeger};&:hover{border-color:${colors.or}}`

const ChipGroup = styled.div`display:flex;flex-wrap:wrap;gap:7px;margin-top:4px`
const Chip = styled.button`font-family:${colors.font};font-size:.78rem;font-weight:600;padding:6px 14px;border-radius:20px;cursor:pointer;transition:all .18s;border:1.5px solid;border-color:${p=>p.$active?colors.vertOlive:'#d0c4b0'};background:${p=>p.$active?'rgba(58,90,46,.08)':'transparent'};color:${p=>p.$active?colors.vertOlive:colors.texteLeger};&:hover{border-color:${colors.vertOlive}}`

const NavRow = styled.div`display:flex;justify-content:${p=>p.$start?'flex-end':'space-between'};margin-top:28px;gap:12px`
const BtnBack = styled.button`background:transparent;border:1.5px solid #c8a090;color:${colors.greentext};padding:11px 22px;border-radius:8px;font-family:${colors.font};font-size:.86rem;font-weight:600;cursor:pointer;transition:all .2s;&:hover{border-color:${colors.primary};color:${colors.primary}}`
const BtnNext = styled.button`background:${colors.vertOlive};color:white;border:none;padding:11px 28px;border-radius:8px;font-family:${colors.font};font-size:.88rem;font-weight:700;letter-spacing:.06em;cursor:pointer;transition:all .25s;display:flex;align-items:center;gap:8px;&:hover{background:#2a4520;transform:translateY(-1px);box-shadow:0 6px 18px rgba(58,90,46,.25)};&:disabled{opacity:.35;cursor:not-allowed;transform:none;box-shadow:none}`
const BtnSubmit = styled(BtnNext)`background:${colors.or};color:${colors.texte};&:hover{background:${colors.orClair};box-shadow:0 6px 20px rgba(200,168,75,.4)}`

const RecapBox = styled.div`background:#fff;border:1px solid #e8d5b8;border-radius:10px;padding:18px;margin-bottom:18px`
const RecapRow = styled.div`display:flex;justify-content:space-between;align-items:center;padding:7px 0;border-bottom:1px solid #f0e8d8;&:last-child{border-bottom:none};font-family:${colors.font};font-size:.85rem;flex-wrap:wrap;gap:4px`
const RecapKey = styled.span`color:${colors.greentext};opacity:.7;flex-shrink:0`
const RecapVal = styled.span`color:${colors.primary};font-weight:600;text-align:right`
const RecapEdit = styled.button`font-family:${colors.font};font-size:.7rem;font-weight:600;color:${colors.or};background:none;border:none;cursor:pointer;text-decoration:underline;margin-left:8px;&:hover{color:${colors.orClair}}`

const SuccessWrap = styled.div`padding:52px 40px;text-align:center;animation:${fadeIn} .5s ease both`
const SuccessIcon = styled.div`width:76px;height:76px;background:${colors.vertOlive};border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:2rem;margin:0 auto 22px;animation:${checkAnim} .5s .1s ease both`
const SuccessTitle = styled.h2`font-family:'Playfair Display',Georgia,serif;font-size:1.6rem;font-weight:700;color:${colors.primary};margin-bottom:10px`
const SuccessSub = styled.p`color:${colors.greentext};opacity:.75;font-size:.93rem;font-family:${colors.font};line-height:1.65;max-width:400px;margin:0 auto`

// ─── DONNÉES ───
const FREQUENCES = ['Ponctuelle', 'Mensuelle', 'Trimestrielle', 'Saisonnière']
const ELEVAGES   = ['Chevaux', 'Bovins', 'Vaches laitières','Autre']
const STEPS = [{ label: 'Coordonnées' }, { label: 'Produits' }, { label: 'Livraison' }, { label: 'Confirmation' }]
const STORAGE_KEY = 'achatpaille_devis_draft'

const validate = {
  Nom:       v => v.trim().length >= 2 ? null : "Merci d'indiquer votre nom",
  Email:     v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Adresse email invalide',
  Téléphone: v => v.replace(/\s/g,'').length >= 8 ? null : 'Numéro trop court',
}

function defaultForm() {
  return {
    Nom:'', Email:'', Téléphone:'', Société:'',
    produits:[], coupes:{},
    Quantité:'', QuantitéAutre:false, Message:'',
    Canton:'', CodePostal:'', Localisation:'', DateLivraison:'',
    Fréquence:'', TypeElevage:[],
  }
}

function FormDevis() {
  const [step, setStep]   = useState(0)
  const [dir,  setDir]    = useState('next')
  const [sent, setSent]   = useState(false)
  const [saved, setSaved] = useState(false)
  const [touched, setTouched] = useState({})
  const [allProducts, setAllProducts] = useState([])
  const [form, setForm] = useState(() => {
    try { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : defaultForm() }
    catch { return defaultForm() }
  })

  // Chargement des produits depuis Supabase
  useEffect(() => {
    supabase.from('products').select('*').eq('available', true).order('created_at').then(({ data }) => {
      setAllProducts((data || []).map(p => ({
        id: p.id,
        name: p.name,
        format: p.format || '',
        icon: p.name.toLowerCase().includes('luzerne') ? '🌱' : p.name.toLowerCase().includes('foin') || p.name.toLowerCase().includes('prairie') ? '🌿' : '🌾',
        coupes: p.coupes || [],
        waitlist: stockStatusOf(p) === 'waitlist',
      })))
    })
  }, [])

  // Sauvegarde auto silencieuse
  useEffect(() => {
    if (sent) return
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form))
  }, [form, sent])

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }))
  const touch = f => setTouched(p => ({ ...p, [f]: true }))

  const toggleProduct = id => {
    setForm(p => {
      const sel = p.produits.includes(id)
      const c = { ...p.coupes }; if (sel) delete c[id]
      return { ...p, produits: sel ? p.produits.filter(x => x !== id) : [...p.produits, id], coupes: c }
    })
  }
  const setCoupe = (pid, c) => setForm(p => ({ ...p, coupes: { ...p.coupes, [pid]: p.coupes[pid] === c ? '' : c } }))
  const toggleElevage = v => setForm(p => ({ ...p, TypeElevage: p.TypeElevage.includes(v) ? p.TypeElevage.filter(x => x !== v) : [...p.TypeElevage, v] }))

  const errors = { Nom: validate.Nom(form.Nom), Email: validate.Email(form.Email), Téléphone: validate.Téléphone(form.Téléphone) }

  const selectedLabels = form.produits.map(id => {
    const p = allProducts.find(x => x.id === id); if (!p) return ''
    const nom = p.name
    const c = form.coupes[id]; return c ? `${nom} (${c})` : nom
  }).filter(Boolean).join(', ')

  const canNext = [
    !errors.Nom && !errors.Email && !errors.Téléphone && form.Nom && form.Email && form.Téléphone,
    form.produits.length > 0,
    form.Canton && form.CodePostal,
    true,
  ][step]

  const goNext = n => {
    setDir('next')
    setSaved(true)
    setTimeout(() => { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }) }, 0)
    setTimeout(() => setSaved(false), 2500)
  }
  const goBack = n => { setDir('back'); setTimeout(() => { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }) }, 0) }

  /* La demande vit dans l'admin ; le mail n'en est que l'alerte. Deux
     tentatives, parce qu'un insert perdu est une demande qui n'existe
     nulle part. supabase.insert() ne lève pas d'exception, il renvoie
     l'erreur dans sa réponse : l'ancien try/catch ne pouvait rien
     attraper, et l'échec passait pour un succès. */
  const enregistrerDemande = async () => {
    const parts = [
      selectedLabels && `Produits : ${selectedLabels}`,
      form.Quantité && `Quantité : ${form.Quantité}`,
      form.Fréquence && `Fréquence : ${form.Fréquence}`,
      form.Canton && `Canton : ${form.Canton}`,
      form.CodePostal && `Code postal : ${form.CodePostal}`,
      form.Localisation && `Localité : ${form.Localisation}`,
      form.DateLivraison && `Date souhaitée : ${form.DateLivraison}`,
      form.TypeElevage.length && `Élevage : ${form.TypeElevage.join(', ')}`,
      form.Message && `Message : ${form.Message}`,
    ].filter(Boolean)
    const ligne = {
      email: form.Email.trim().toLowerCase(),
      name: form.Nom,
      phone: form.Téléphone,
      message: parts.join('\n'),
      status: 'sent',
    }
    for (let essai = 0; essai < 2; essai++) {
      try {
        const { error } = await supabase.from('quotes').insert([ligne])
        if (!error) return true
      } catch {}
    }
    return false
  }

  const handleSubmit = async () => {
    const fd = new FormData()
    fd.append('Nom', form.Nom); fd.append('Email', form.Email); fd.append('Téléphone', form.Téléphone)
    if (form.Société)            fd.append('Société', form.Société)
    fd.append('Produits', selectedLabels)
    Object.entries(form.coupes).forEach(([id, c]) => { if (c) { const p = allProducts.find(x => x.id === id); if (p) fd.append(`Coupe_${p.name}`, c) } })
    if (form.Quantité) fd.append('Quantité', form.Quantité.startsWith('Autre:') ? form.Quantité.replace('Autre:','').trim() : form.Quantité)
    if (form.DateLivraison)      fd.append('Date de livraison', form.DateLivraison)
    if (form.Fréquence)          fd.append('Fréquence', form.Fréquence)
    if (form.TypeElevage.length) fd.append("Type d'élevage", form.TypeElevage.join(', '))
    fd.append('Canton', form.Canton)
    if (form.CodePostal)    fd.append('Code postal', form.CodePostal)
    if (form.Localisation)  fd.append('Localité', form.Localisation)
    if (form.Message)       fd.append('Message', form.Message)

    /* La base d'abord, le mail ensuite, pour que le mail puisse porter
       l'échec : sans ce drapeau, une demande reçue par mail mais absente
       de l'admin ne se remarque qu'en la cherchant. */
    const enregistree = await enregistrerDemande()
    if (!enregistree) {
      fd.append('⚠️ Admin', "ÉCHEC D'ENREGISTREMENT — cette demande n'est pas dans l'admin, à saisir à la main")
    }
    try { await fetch('https://formspree.io/f/xblyrzvk', { method: 'POST', body: fd, headers: { Accept: 'application/json' } }) } catch {}
    localStorage.removeItem(STORAGE_KEY)
    setSent(true)
  }

  const Hero = (
    <MiniHero><HeroBg /><HeroGrain />
      <HeroContent>
        <HeroBadge>🌾 Devis gratuit</HeroBadge>
        <HeroTitle>Demandez votre <em>devis</em></HeroTitle>
        <HeroSub>Réponse personnalisée sous 24h · Sans engagement</HeroSub>
      </HeroContent><HeroBar />
    </MiniHero>
  )

  if (sent) return <>{Hero}<PageWrap><Card><SuccessWrap>
    <SuccessIcon>✓</SuccessIcon>
    <SuccessTitle>Demande envoyée !</SuccessTitle>
    <SuccessSub>Merci {form.Nom ? form.Nom.split(' ')[0] : ''} ! Votre demande a bien été reçue. Nous revenons vers vous sous 24h ouvrées avec une offre personnalisée. Vous recevrez un email de confirmation dès que nous aurons pris en charge votre demande.</SuccessSub>
    <div style={{
      marginTop: 28, background: 'rgba(58,90,46,0.06)',
      border: '1.5px solid rgba(58,90,46,0.15)', borderRadius: 12,
      padding: '18px 22px', maxWidth: 400, margin: '28px auto 0',
    }}>
      <div style={{ fontSize: '1.3rem', marginBottom: 8 }}>🔍</div>
      <div style={{ fontFamily: colors.font, fontWeight: 700, fontSize: '0.9rem', color: colors.texte, marginBottom: 6 }}>
        Suivez votre demande en temps réel
      </div>
      <div style={{ fontFamily: colors.font, fontSize: '0.82rem', color: colors.texteLeger, lineHeight: 1.6, marginBottom: 14 }}>
        Retrouvez à tout moment l'avancement de votre devis depuis votre email.
      </div>
      <a href={`/suivi-devis?email=${encodeURIComponent(form.Email || '')}`} style={{
        display: 'inline-block', background: colors.vertOlive, color: '#fff',
        textDecoration: 'none', fontFamily: colors.font, fontWeight: 700,
        fontSize: '0.85rem', padding: '10px 22px', borderRadius: 7,
        transition: 'all 0.2s',
      }}>
        Accéder au suivi →
      </a>
    </div>
  </SuccessWrap></Card></PageWrap></>

  const pct = Math.round((step / (STEPS.length - 1)) * 100)

  return (
    <>{Hero}
    <PageWrap><Card>
      <ProgressBar><ProgressFill $pct={pct} /></ProgressBar>
      <ProgressLabel>Étape {step + 1} sur {STEPS.length}</ProgressLabel>
      {saved && <SavedBadge>💾 Brouillon sauvegardé</SavedBadge>}

      <StepperWrap>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.label}>
            <div style={{ display:'flex', alignItems:'center' }}>
              <StepCircle $active={step===i} $done={step>i}>{step>i?'✓':i+1}</StepCircle>
              <StepLabel $active={step===i} $done={step>i}>{s.label}</StepLabel>
            </div>
            {i < STEPS.length-1 && <StepLine $done={step>i} />}
          </React.Fragment>
        ))}
      </StepperWrap>

      {/* ── ÉTAPE 0 ── */}
      {step===0 && <CardBody key="s0" $dir={dir}>
        <StepTitle>Vos coordonnées</StepTitle>
        <StepDesc>Ces informations nous permettent de vous recontacter avec votre devis personnalisé.</StepDesc>
        <FieldGrid $cols={2}>
          <Field>
            <Label>Nom / Prénom *</Label>
            <Input value={form.Nom} onChange={e=>set('Nom',e.target.value)} onBlur={()=>touch('Nom')} placeholder="Jean-Pierre Müller" $error={touched.Nom&&!!errors.Nom} $valid={!errors.Nom&&form.Nom} />
            {touched.Nom&&errors.Nom&&<ErrorMsg>⚠ {errors.Nom}</ErrorMsg>}
          </Field>
          <Field>
            <Label>Email *</Label>
            <Input type="email" value={form.Email} onChange={e=>set('Email',e.target.value)} onBlur={()=>touch('Email')} placeholder="votre@email.ch" $error={touched.Email&&!!errors.Email} $valid={!errors.Email&&form.Email} />
            {touched.Email&&errors.Email&&<ErrorMsg>⚠ {errors.Email}</ErrorMsg>}
          </Field>
          <Field>
            <Label>Téléphone *</Label>
            <Input type="tel" value={form.Téléphone} onChange={e=>set('Téléphone',e.target.value)} onBlur={()=>touch('Téléphone')} placeholder="+41 79 000 00 00" $error={touched.Téléphone&&!!errors.Téléphone} $valid={!errors.Téléphone&&form.Téléphone} />
            {touched.Téléphone&&errors.Téléphone&&<ErrorMsg>⚠ {errors.Téléphone}</ErrorMsg>}
          </Field>
          <Field>
            <Label>Société / Exploitation</Label>
            <Input value={form.Société} onChange={e=>set('Société',e.target.value)} placeholder="Ferme Müller" />
          </Field>
        </FieldGrid>
        <Field style={{marginTop:16}}>
          <Label>Type d'élevage</Label>
          <ChipGroup>{ELEVAGES.map(e=><Chip key={e} type="button" $active={form.TypeElevage.includes(e)} onClick={()=>toggleElevage(e)}>{e}</Chip>)}</ChipGroup>
        </Field>
        <NavRow $start><BtnNext onClick={()=>goNext(1)} disabled={!canNext}>Suivant — Produits <span>→</span></BtnNext></NavRow>
      </CardBody>}

      {/* ── ÉTAPE 1 ── */}
      {step===1 && <CardBody key="s1" $dir={dir}>
        <StepTitle>Vos produits souhaités</StepTitle>
        <StepDesc>Sélectionnez un ou plusieurs produits puis choisissez la coupe si besoin.</StepDesc>
        <ProductGrid>
          {allProducts.map(p => { const sel=form.produits.includes(p.id); return (
            <div key={p.id}>
              <ProductChoice $sel={sel} onClick={()=>toggleProduct(p.id)}>
                <ProdIcon $sel={sel}>{p.icon}</ProdIcon>
                <ProdInfo>
                  <ProdName>{p.name}</ProdName>
                  <ProdType>{p.format || 'Bottes carrées'}</ProdType>
                  {p.waitlist && <ProdStockTag $bg={STOCK_META.waitlist.badge}>{STOCK_META.waitlist.label}</ProdStockTag>}
                </ProdInfo>
                {sel&&<CheckMark>✓</CheckMark>}
              </ProductChoice>
              {sel&&p.coupes.length>0&&<CoupeWrap>
                <CoupeLabel>✂️ Coupe souhaitée</CoupeLabel>
                <CoupeBtns>
                  {p.coupes.map(c=><CoupeBtn key={c} type="button" $active={form.coupes[p.id]===c} onClick={e=>{e.stopPropagation();setCoupe(p.id,c)}}>{c}</CoupeBtn>)}
                  <CoupeBtn type="button" $active={form.coupes[p.id]==='Autre'} onClick={e=>{e.stopPropagation();setCoupe(p.id,'Autre')}}>Autre / À préciser</CoupeBtn>
                </CoupeBtns>
              </CoupeWrap>}
            </div>
          )})}
        </ProductGrid>
        <FieldGrid style={{marginTop:18}}>
          <Field>
            <Label>Quantité estimée</Label>
            <ChipGroup>
              {['1 camion remorque','2 camions remorques','3 camions remorques','4 camions remorques','5 camions remorques'].map(q=>(
                <Chip key={q} type="button"
                  $active={form.Quantité===q}
                  onClick={()=>{set('Quantité', form.Quantité===q?'':q); set('QuantitéAutre',false)}}
                >{q}</Chip>
              ))}
              <Chip type="button" $active={form.QuantitéAutre} onClick={()=>{set('QuantitéAutre',!form.QuantitéAutre); set('Quantité','')}}>Autre</Chip>
            </ChipGroup>
            {form.QuantitéAutre&&(
              <Input style={{marginTop:8}} value={form.Quantité} onChange={e=>set('Quantité',e.target.value)} placeholder="Précisez votre quantité…" />
            )}
          </Field>
          <Field>
            <Label>Fréquence de commande</Label>
            <ChipGroup>{FREQUENCES.map(f=><Chip key={f} type="button" $active={form.Fréquence===f} onClick={()=>set('Fréquence',form.Fréquence===f?'':f)}>{f}</Chip>)}</ChipGroup>
          </Field>
        </FieldGrid>
        <Field style={{marginTop:14}}>
          <Label>Précisions</Label>
          <Textarea value={form.Message} onChange={e=>set('Message',e.target.value)} placeholder="Contraintes de livraison, besoins particuliers…" />
        </Field>
        <NavRow>
          <BtnBack onClick={()=>goBack(0)}>← Retour</BtnBack>
          <BtnNext onClick={()=>goNext(2)} disabled={!canNext}>Suivant — Livraison <span>→</span></BtnNext>
        </NavRow>
      </CardBody>}

      {/* ── ÉTAPE 2 ── */}
      {step===2 && <CardBody key="s2" $dir={dir}>
        <StepTitle>Adresse de livraison</StepTitle>
        <StepDesc>Indiquez où vous souhaitez être livré en Suisse pour qu'on calcule le transport au plus juste.</StepDesc>
        <FieldGrid $cols={2}>
          <Field>
            <Label>Canton *</Label>
            <Select value={form.Canton} onChange={e=>set('Canton',e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {['Valais (VS)','Vaud (VD)','Fribourg (FR)','Berne (BE)','Genève (GE)','Neuchâtel (NE)','Jura (JU)','Soleure (SO)','Argovie (AG)','Zurich (ZH)','Autre'].map(c=><option key={c}>{c}</option>)}
            </Select>
          </Field>
          <Field>
            <Label>Code postal *</Label>
            <Input value={form.CodePostal} onChange={e=>set('CodePostal',e.target.value)} placeholder="Ex : 1950" />
          </Field>
        </FieldGrid>
        <Field style={{marginTop:14}}>
          <Label>Localité / Adresse</Label>
          <Input value={form.Localisation} onChange={e=>set('Localisation',e.target.value)} placeholder="Ex : Sion, route de la ferme 12" />
        </Field>
        <Field style={{marginTop:14}}>
          <Label>Date de livraison souhaitée <Optional>(+/- 3 jours)</Optional></Label>
          <Input type="date" value={form.DateLivraison} onChange={e=>set('DateLivraison',e.target.value)} min={new Date().toISOString().split('T')[0]} />
        </Field>
        <NavRow>
          <BtnBack onClick={()=>goBack(1)}>← Retour</BtnBack>
          <BtnNext onClick={()=>goNext(3)} disabled={!canNext}>Suivant — Confirmation <span>→</span></BtnNext>
        </NavRow>
      </CardBody>}

      {/* ── ÉTAPE 3 ── */}
      {step===3 && <CardBody key="s3" $dir={dir}>
        <StepTitle>Récapitulatif</StepTitle>
        <StepDesc>Vérifiez vos informations avant d'envoyer votre demande de devis.</StepDesc>
        <RecapBox>
          {[
            {k:'Nom / Prénom',v:form.Nom,s:0},
            {k:'Email',v:form.Email,s:0},
            {k:'Téléphone',v:form.Téléphone,s:0},
            form.Société&&{k:'Société',v:form.Société,s:0},
            form.TypeElevage.length&&{k:"Type d'élevage",v:form.TypeElevage.join(', '),s:0},
            {k:'Produits',v:selectedLabels||'—',s:1},
            form.Quantité&&{k:'Quantité',v:form.Quantité,s:1},
            form.Fréquence&&{k:'Fréquence',v:form.Fréquence,s:1},
            {k:'Canton',v:form.Canton||'—',s:2},
            form.CodePostal&&{k:'Code postal',v:form.CodePostal,s:2},
            form.Localisation&&{k:'Localité',v:form.Localisation,s:2},
            form.DateLivraison&&{k:'Date souhaitée',v:new Date(form.DateLivraison).toLocaleDateString('fr-FR'),s:2},
          ].filter(Boolean).map(row=>(
            <RecapRow key={row.k}>
              <RecapKey>{row.k}</RecapKey>
              <div style={{display:'flex',alignItems:'center'}}>
                <RecapVal>{row.v}</RecapVal>
                <RecapEdit onClick={()=>goBack(row.s)}>modifier</RecapEdit>
              </div>
            </RecapRow>
          ))}
        </RecapBox>
        <NavRow>
          <BtnBack onClick={()=>goBack(2)}>← Modifier</BtnBack>
          <BtnSubmit onClick={handleSubmit}>✓ &nbsp;Envoyer ma demande</BtnSubmit>
        </NavRow>
      </CardBody>}

    </Card></PageWrap></>
  )
}

export default FormDevis
